import { FileServiceProvider } from '@magicdoorinc/ui';
import { Routes, Route } from '@solidjs/router';
import { ErrorBoundary, lazy } from 'solid-js';
import DevToolbar from '~/components/common/DevToolbar';
import NotFound from '~/components/common/NotFound';
import { CompanyTypeConfirm } from '~/components/company-type-confirm';
import { FullScreenLayout, Layout } from '~/components/layouts';
import { MagicTagsMobile } from '~/components/magic-tags-v2/MagicTagsMobile';
import Navigator from '~/framework/Navigator';
import ErrorFallback from '~/pages/error/ErrorFallback';
import { ShowErrorFallback } from '~/pages/test/ShowErrorFallback';
import UnauthorizedPage from '~/pages/test/UnauthorizedPage';
import UnknownErrorPage from '~/pages/test/UnknownErrorPage';
import { ErrorHandler } from '~/pdfsigner/ui/context/ErrorContext';
import { fileService } from '~/utils/file';
import type { Component } from 'solid-js';

const DashboardPage = lazy(() => import('~/pages/dashboard/Dashboard').then((m) => ({ default: m.DashboardPage })));
const AiRunbooksRoutes = lazy(() => import('~/pages/ai-runbooks').then((m) => ({ default: m.AiRunbooksRoutes })));
const AiRoutes = lazy(() => import('~/pages/ai').then((m) => ({ default: m.AiRoutes })));
const ViolationsRoutes = lazy(() => import('~/pages/violations').then((m) => ({ default: m.ViolationsRoutes })));
const ArchitecturalRequestsRoutes = lazy(() =>
  import('~/pages/architectural-requests').then((m) => ({ default: m.ArchitecturalRequestsRoutes }))
);
const BoardMembersRoutes = lazy(() => import('~/pages/board-members').then((m) => ({ default: m.BoardMembersRoutes })));
const SurveysRoutes = lazy(() => import('~/pages/surveys').then((m) => ({ default: m.SurveysRoutes })));
const AddOfflinePaymentRoutes = lazy(() => import('~/pages/add-offline-payment').then((m) => ({ default: m.AddOfflinePaymentRoutes })));
const AnnouncementRoutes = lazy(() => import('~/pages/announcements').then((m) => ({ default: m.default })));
const ArchiveMessagesRoutes = lazy(() => import('~/pages/archive-messages').then((m) => ({ default: m.default })));
const BankAccountsRoutes = lazy(() => import('~/pages/bank-accounts').then((m) => ({ default: m.BankAccountsRoutes })));
const BankLedgerTransfersRoutes = lazy(() =>
  import('~/pages/bank-ledger-transfers').then((m) => ({ default: m.BankLedgerTransfersRoutes }))
);
const BillsRoutes = lazy(() => import('~/pages/bills').then((m) => ({ default: m.BillsRoutes })));
const ChartOfAccountRoutes = lazy(() => import('~/pages/chart-of-accounts').then((m) => ({ default: m.ChartOfAccountRoutes })));
const ChatRoutes = lazy(() => import('~/pages/chats').then((m) => ({ default: m.default })));
const ChecksRoutes = lazy(() => import('~/pages/checks').then((m) => ({ default: m.ChecksRoutes })));
const CompanyRevenueRoutes = lazy(() => import('~/pages/company-revenue').then((m) => ({ default: m.CompanyRevenueRoutes })));
const DepositSlipsRoutes = lazy(() => import('~/pages/deposit-slips').then((m) => ({ default: m.DepositSlipsRoutes })));
const ManualEntriesRoutes = lazy(() => import('~/pages/manual-entries').then((m) => ({ default: m.ManualEntriesRoutes })));
const MagicdoorRoutes = lazy(() => import('~/pages/magicdoor').then((m) => ({ default: m.MagicdoorRoutes })));
const LeaseTemplateRoutes = lazy(() => import('~/pages/lease-templates').then((m) => ({ default: m.LeaseTemplateRoutes })));
const LeasesRoutes = lazy(() => import('~/pages/leases').then((m) => ({ default: m.LeasesRoutes })));
const ListingRoutes = lazy(() => import('~/pages/listing').then((m) => ({ default: m.default })));
const OnboardingRoutes = lazy(() => import('~/pages/onboarding').then((m) => ({ default: m.OnboardingRoutes })));
const MaintenanceRequestRoutes = lazy(() => import('~/pages/maintenance-request').then((m) => ({ default: m.default })));
const OwnerRoutes = lazy(() => import('~/pages/owner').then((m) => ({ default: m.OwnerRoutes })));
const OwnerFundsRoutes = lazy(() => import('~/pages/owner-funds').then((m) => ({ default: m.OwnerFundsRoutes })));
const PortfoliosRoutes = lazy(() => import('~/pages/portfolios').then((m) => ({ default: m.PortfoliosRoutes })));
const PropertyManagerRoutes = lazy(() => import('~/pages/property-managers').then((m) => ({ default: m.PropertyManagerRoutes })));
const RentalApplicationRoutes = lazy(() => import('~/pages/rental-applications').then((m) => ({ default: m.RentalApplicationRoutes })));
const ReportsRoutes = lazy(() => import('~/pages/reports').then((m) => ({ default: m.default })));
const SettingRoutes = lazy(() => import('~/pages/settings').then((m) => ({ default: m.default })));
const TenantsRoutes = lazy(() => import('~/pages/tenants').then((m) => ({ default: m.TenantsRoutes })));
const UnreadMessagesRoutes = lazy(() => import('~/pages/unread-messages').then((m) => ({ default: m.default })));
const VendorRoutes = lazy(() => import('~/pages/vendors').then((m) => ({ default: m.default })));
const WorkOrderRoutes = lazy(() => import('~/pages/work-orders').then((m) => ({ default: m.WorkOrderRoutes })));
const SubsidizersRoutes = lazy(() => import('~/pages/subsidizers').then((m) => ({ default: m.SubsidizersRoutes })));
const LeaseRenewalRoutes = lazy(() => import('~/pages/lease-renewal').then((m) => ({ default: m.LeaseRenewalRoutes })));
const InsuranceRoutes = lazy(() => import('~/pages/insurance').then((m) => ({ default: m.InsuranceRoutes })));
const LetterTemplateRoutes = lazy(() => import('~/pages/letter-templates').then((m) => ({ default: m.LetterTemplateRoutes })));
const DocumentTemplateRoutes = lazy(() => import('~/pages/document-templates').then((m) => ({ default: m.DocumentTemplateRoutes })));
const IconsPage = lazy(() => import('~/pages/test/IconsPage').then((m) => ({ default: m.IconsPage })));
const MaintenanceTestPage = lazy(() => import('~/pages/test/MaintenanceTestPage').then((m) => ({ default: m.default })));
const PlaygroundPage = lazy(() => import('~/pages/test/PlaygroundPage').then((m) => ({ default: m.PlaygroundPage })));
const UiTestPage = lazy(() => import('~/pages/test/ui-test/UiTestPage').then((m) => ({ default: m.default })));

const App: Component = () => {
  return (
    <ErrorBoundary
      fallback={(err, reset) => {
        return <ErrorFallback error={err} reset={reset} />;
      }}>
      <FileServiceProvider fileService={fileService}>
        <CompanyTypeConfirm />
        <ErrorHandler>
          <Routes>
            <Navigator />
            <Route path="/" component={Layout}>
              <Route path="/" component={DashboardPage} />
              <Route path="/onboarding/*step" component={OnboardingRoutes} />
              <Route path="/listings/*" component={ListingRoutes} />
              <Route path="/accounting">
                <Route path="/bank-accounts/*" component={BankAccountsRoutes} />
                <Route path="/bank-ledger-transfers/*" component={BankLedgerTransfersRoutes} />
                <Route path="/bills/*" component={BillsRoutes} />
                <Route path="/chart-of-accounts/*" component={ChartOfAccountRoutes} />
                <Route path="/company-revenue/*" component={CompanyRevenueRoutes} />
                <Route path="/magicdoor/*" component={MagicdoorRoutes} />
                <Route path="/owner-funds/*" component={OwnerFundsRoutes} />
                <Route path="/deposit-slips/*" component={DepositSlipsRoutes} />
                <Route path="/manual-entries/*" component={ManualEntriesRoutes} />
                <Route path="/checks/*" component={ChecksRoutes} />
              </Route>
              <Route path="/reports/*" component={ReportsRoutes} />
              <Route path="/users">
                <Route path="/tenants/*" component={TenantsRoutes} />
                <Route path="/property-managers/*" component={PropertyManagerRoutes} />
                <Route path="/owners/*" component={OwnerRoutes} />
              </Route>
              <Route path="/maintenance">
                <Route path="/vendors/*" component={VendorRoutes} />
                <Route path="/maintenance-request/*" component={MaintenanceRequestRoutes} />
                <Route path="/work-orders/*" component={WorkOrderRoutes} />
                <Route path="/ai-runbooks/*" component={AiRunbooksRoutes} />
              </Route>
              <Route path="/communications">
                <Route path="/announcements/*" component={AnnouncementRoutes} />
                <Route path="/chats/*" component={ChatRoutes} />
                <Route path="/dashboard/*" component={UnreadMessagesRoutes} />
                <Route path="/archive-messages/*" component={ArchiveMessagesRoutes} />
                <Route path="/templates/*" component={LetterTemplateRoutes} />
                <Route path="/document-templates/*" component={DocumentTemplateRoutes} />
              </Route>
              <Route path="/ai/*" component={AiRoutes} />
              <Route path="/associations">
                <Route path="/violations/*" component={ViolationsRoutes} />
                <Route path="/architectural-requests/*" component={ArchitecturalRequestsRoutes} />
                <Route path="/board-members/*" component={BoardMembersRoutes} />
                <Route path="/surveys/*" component={SurveysRoutes} />
              </Route>
              <Route path="/portfolios/*" component={PortfoliosRoutes} />
              <Route path="/leasing">
                <Route path="/leases/*" component={LeasesRoutes} />
                <Route path="/lease-templates/*" component={LeaseTemplateRoutes} />
                <Route path="/insurance/*" component={InsuranceRoutes} />
                <Route path="/add-offline-payment/*" component={AddOfflinePaymentRoutes} />
                <Route path="/subsidizers/*" component={SubsidizersRoutes} />
                <Route path="/lease-renewal/*" component={LeaseRenewalRoutes} />
                <Route path="/*" component={RentalApplicationRoutes} />
              </Route>
              <Route path="/magic-tags/*" component={MagicTagsMobile} />
              <Route path="/settings/*" component={SettingRoutes} />
            </Route>

            <Route path="/test">
              <Route path="ui" component={UiTestPage} />
              <Route path="unauthorized" component={UnauthorizedPage} />
              <Route path="unknown" component={UnknownErrorPage} />
              <Route path="play" component={PlaygroundPage} />
              <Route path="icons" component={IconsPage} />
              <Route path="error" component={ShowErrorFallback} />
              <Route path="maintenance" component={MaintenanceTestPage} />
            </Route>
            <Route path="*" component={FullScreenLayout}>
              <Route path="*" component={NotFound} />
            </Route>
          </Routes>
          <DevToolbar />
        </ErrorHandler>
      </FileServiceProvider>
    </ErrorBoundary>
  );
};

export default App;
